import * as emailApi from '@/services/emailService';
import type { SagaIterator } from 'redux-saga';
import { call, put, select } from 'redux-saga/effects';
import dayjs from 'dayjs';
import { sendAnalytics } from '@/services/analyticsService';
import * as userActions from '../user/userActions';
import * as dashboardActions from '../dashboard/dashboardActions';
import * as actions from './emailActions';
import type { EmailAction } from './emailTypes';

export function* sendEmailsSaga({ payload }: EmailAction): SagaIterator {
	const emailInfo = payload.emails[0];

	const getEmailType = (
		email: string,
		business: any,
		emailObject: any
	): string => {
		if (emailObject.userAdded && emailObject.userAdded.exists) {
			return 'userAddedEmail';
		}
		const primaryEmail = business?.facebook?.email
			? String(business?.facebook?.email).toLowerCase()
			: '';

		const secondaryEmail = business?.email
			? String(business?.email).toLowerCase()
			: '';

		const thirdEmail = business?.web?.email
			? String(business?.web?.email).toLowerCase()
			: '';

		if (email === primaryEmail) {
			return 'emailPrimary';
		}

		if (email === secondaryEmail) {
			return 'emailSecondary';
		}

		if (email === thirdEmail) {
			return 'thirdEmail';
		}

		return '';
	};

	const emailType = getEmailType(
		emailInfo.email,
		emailInfo.business,
		emailInfo
	);
	emailInfo.emailType = emailType;

	try {
		if (emailInfo.business.threadedEmails) {
			emailInfo.business.threadedEmails = [];
		}

		const response = yield call(emailApi.sendEmails, payload);
		if (response.status !== 200) {
			if (response.config.data.includes('scheduleDate')) {
				yield put(
					actions.emailSendScheduledEmailFailure(response.data)
				);
				return;
			}

			yield put(
				actions.emailSendEmailsFailure({
					message: response.data.data.message,
					businessId: emailInfo.businessId
				})
			);
			return;
		}
		if (response.problem) {
			throw new Error('Error: Email limit reached');
		}

		const templates = yield select(state => {
			return state.email.getIn(['templates', 'data']).toJS();
		});
		if (emailInfo.templateId) {
			yield put(
				actions.emailIncreaseEmailTemplateUsage(
					response.data.data.result[0]?.templateId
				)
			);
		}

		const { emailLimitLeft } = yield select(state => {
			return state.user.getIn(['authorize', 'user']).toJS();
		});

		if (
			emailLimitLeft > 0 &&
			!response.data.data.result[0].isScheduledEmail &&
			!payload.isReply
		) {
			yield put(userActions.userUpdateEmailsLeft());
		}

		if (
			response.data.data.scheduleDate &&
			response.data.data.result[0].isScheduledEmail
		) {
			const {
				data: { data }
			} = response;
			const scheduledPayload = {
				businessId: data.result[0].businessId,
				scheduleDate: data.scheduleDate,
				requestedScheduleDate: data.requestedScheduleDate
			};
			yield put(actions.emailSendScheduledEmailSuccess(scheduledPayload));
			yield put(dashboardActions.setSentEmailsInBusinesses(data.result));

			if (
				dayjs().format('MM-DD-YYYY') ===
				dayjs(data.scheduleDate, 'MM/DD/YYYY, h:mm:ss A').format(
					'MM-DD-YYYY'
				)
			) {
				yield put(userActions.userUpdateEmailsLeft());
			}

			return;
		}

		const user = yield select(state =>
			state.user.getIn(['authorize', 'user']).toJS()
		);

		if (user && user.email) {
			sendAnalytics({
				type: 'track',
				userId: user.email,
				eventName: 'User emailed a business using a list',
				properties: {
					businessId: emailInfo.businessId,
					businessName: emailInfo.name,
					businessEmail: emailInfo.email,
					businessAddress: emailInfo.business.address,
					userAddedEmail: emailInfo.userAdded.exists
				}
			});
		}

		const data = response.data.data.result;
		const { nylasThreadId } = response.data.data;
		data[0]['businessId'] = emailInfo['businessId'];
		data[0]['nylasThreadId'] = nylasThreadId ?? '';
		if (emailInfo.userAdded) {
			data[0]['userAddedData'] = emailInfo.userAdded;
		}
		yield put(actions.emailSendEmailsSuccess(data));

		yield put(dashboardActions.setSentEmailsInBusinesses(data));
	} catch (error) {
		yield put(
			actions.emailSendEmailsFailure({
				message: error,
				businessId: emailInfo.businessId
			})
		);
	}
}

export function* getEmailTemplatesSaga(): SagaIterator {
	try {
		const { data } = yield call(emailApi.getEmailTemplates);

		yield put(actions.emailGetEmailTemplatesSuccess(data));
	} catch (error) {
		yield put(actions.emailGetEmailTemplatesFailure(error));
	}
}

export function* createEmailTemplateSaga({
	payload
}: EmailAction): SagaIterator {
	try {
		const { data } = yield call(emailApi.insertEmailTemplate, payload);
		yield put(actions.emailCreateEmailTemplateSuccess(data));
	} catch (error) {
		yield put(actions.emailCreateEmailTemplateFailure(error));
	}
}

export function* updateEmailTemplateSaga({
	payload
}: EmailAction): SagaIterator {
	try {
		const response = yield call(emailApi.updateEmailTemplate, payload);

		if (response.status !== 200) {
			yield put(actions.emailUpdateEmailTemplateFailure());
			return;
		}

		yield put(
			actions.emailUpdateEmailTemplateSuccess({
				template: payload,
				templateEditDate: response.data.data.templateEditDate
			})
		);
	} catch (error) {
		yield put(actions.emailUpdateEmailTemplateFailure());
	}
}

export function* deleteEmailTemplateSaga({
	payload
}: EmailAction): SagaIterator {
	try {
		yield call(emailApi.deleteEmailTemplate, payload.id);

		yield put(actions.emailDeleteEmailTemplateSuccess());
	} catch (error) {
		yield put(actions.emailDeleteEmailTemplateFailure(error));
	}
}

export function* duplicateEmailTemplateSaga({
	payload
}: EmailAction): SagaIterator {
	try {
		yield call(emailApi.duplicateEmailTemplate, payload);

		yield put(actions.emailDuplicateEmailTemplateSuccess());
	} catch (error) {
		yield put(actions.emailDuplicateEmailTemplateFailure(error));
	}
}

export function* emailGetEmailThread({ payload }: EmailAction): SagaIterator {
	const { userEmail, businessId } = payload;
	try {
		yield put(dashboardActions.emailGetEmailThreadLoading());
		const { data } = yield call(emailApi.getEmailThread, {
			userEmail,
			businessId
		});
		yield put(
			dashboardActions.emailGetEmailThreadSuccess({
				thread: data['thread'],
				businessId
			})
		);
		yield put(userActions.userGetNewCommunications());
	} catch (error) {
		yield put(dashboardActions.emailGetEmailThreadFailure());
	}
}

export function* emailGetEmailContactedStatus({
	payload
}: EmailAction): SagaIterator {
	const { toEmail } = payload;
	try {
		yield put(actions.emailGetEmailContactedStatusLoading());
		const { data } = yield call(emailApi.getEmailContactedStatus, {
			toEmail
		});

		yield put(actions.emailGetEmailContactedStatusSuccess(data));
	} catch (error) {
		yield put(actions.emailGetEmailContactedStatusFailure());
	}
}

export function* emailGetEmailStatuses({ payload }: EmailAction): SagaIterator {
	try {
		yield put(actions.emailGetEmailStatusesLoading());
		const {
			data: {
				data: { result }
			}
		} = yield call(emailApi.getEmailStatuses);
		yield put(actions.emailGetEmailStatusesSuccess(result[0]));
	} catch (error) {
		yield put(actions.emailGetEmailStatusesFailure(error));
	}
}

export function* getScheduledEmailLimits({
	payload
}: EmailAction): SagaIterator {
	try {
		const response = yield call(emailApi.getScheduledEmailLimits);
		yield put(
			actions.emailGetScheduledEmailLimitsSuccess(response.data.data)
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* getScheduledEmails({ payload }: EmailAction): SagaIterator {
	try {
		const response = yield call(emailApi.getScheduledEmails, payload);

		yield put(actions.emailGetScheduledEmailsSuccess(response.data.data));
	} catch (error) {
		throw new Error(error);
	}
}

export function* cancelScheduledBatchEmailsAdmin({
	payload
}: EmailAction): SagaIterator {
	try {
		const { userId, startDate, endDate } = payload;
		const response = yield call(emailApi.cancelScheduledBatchEmailsAdmin, {
			userId,
			startDate,
			endDate
		});

		if (response.status !== 200) {
			yield put(
				actions.emailCancelScheduledBatchEmailsAdminFailure(response)
			);
		}

		const { failedCancellations } = response.data.data;
		const { successfulCancellations } = response.data.data;

		yield put(
			actions.emailCancelScheduledBatchEmailsAdminSuccess({
				failedCancellations,
				successfulCancellations
			})
		);
	} catch (error) {
		yield put(actions.emailCancelScheduledBatchEmailsAdminFailure(error));
	}
}

export function* cancelScheduledBatchEmailsUser({
	payload
}: EmailAction): SagaIterator {
	try {
		const response = yield call(
			emailApi.cancelScheduledBatchEmailsUser,
			payload
		);
		const { successfulCancellations } = response.data.data;

		if (response.status !== 200 || successfulCancellations.length === 0) {
			yield put(actions.emailCancelScheduledBatchEmailsUserFailure());
			return;
		}

		yield put(
			actions.emailCancelScheduledBatchEmailsUserSuccess(
				successfulCancellations
			)
		);
	} catch (error) {
		yield put(actions.emailCancelScheduledBatchEmailsUserFailure());
	}
}

export function* getScheduledEmailsCountByDate({
	payload
}: EmailAction): SagaIterator {
	try {
		const response = yield call(
			emailApi.getScheduledEmailsCountByDate,
			payload
		);

		yield put(
			actions.emailGetScheduledEmailsCountByDateSuccess(
				Number(response.data.data)
			)
		);
	} catch (error) {}
}

export function* getScheduledEmailsCountByTimePeriod({
	payload
}: EmailAction): SagaIterator {
	try {
		const response = yield call(
			emailApi.getScheduledEmailsCountByTimePeriod,
			payload
		);

		const reformattedArray = response.data.data.map(x => {
			return {
				date: new Date(x.startDate).getTime(),
				value: x.emailsInRange
			};
		});

		yield put(
			actions.emailGetScheduledEmailsCountByTimePeriodSuccess(
				reformattedArray
			)
		);
	} catch (error) {}
}

export function* cancelScheduledEmail({ payload }: EmailAction): SagaIterator {
	try {
		const { businessId, listId, scheduleDate } = payload;
		const response = yield call(emailApi.cancelScheduledEmail, {
			businessId,
			listId
		});
		if (response.status !== 200) {
			yield put(actions.emailCancelScheduledEmailFailure());
			return;
		}

		if (
			dayjs().format('MM-DD-YYYY') ===
			dayjs(scheduleDate, 'MM/DD/YYYY, h:mm:ss A').format('MM-DD-YYYY')
		) {
			yield put(userActions.userIncreaseEmailsLeft());
		}

		yield put(
			actions.emailCancelScheduledEmailSuccess({
				businessId,
				data: response.data.data
			})
		);
	} catch (error) {
		yield put(actions.emailCancelScheduledEmailFailure());
		throw new Error(error);
	}
}

export function* getEmailTemplateScores({
	payload
}: EmailAction): SagaIterator {
	try {
		const { id, subject, body } = payload;

		// Structure the payload dynamically based on the feature flag
		const requestPayload = { emailSubject: subject, emailBody: body }; // Redesign structure

		// Call the appropriate API endpoint
		const response = yield call(
			emailApi.getEmailTemplateScoresRedesign,
			requestPayload
		);

		if (response.status !== 200) {
			yield put(actions.emailGetEmailTemplateScoresFailure());
			return;
		}

		// Dynamically decide the payload structure based on `isRedesign`
		const responseData = response.data;

		yield put(
			actions.emailGetEmailTemplateScoresSuccess({
				id,
				data: responseData
			})
		);
	} catch (error) {
		yield put(actions.emailGetEmailTemplateScoresFailure());
	}
}

export function* sendAll({ payload }: EmailAction): SagaIterator {
	try {
		const response = yield call(emailApi.sendAll, payload);

		if (response.status !== 200) {
			if (response?.data?.data?.message) {
				yield put(
					actions.emailSendAllFailure(response?.data?.data?.message)
				);
			} else {
				yield put(actions.emailSendAllFailure('Network error.'));
			}
			return;
		}

		yield put(actions.emailSendAllSuccess(response.data.data));

		if (response?.data?.data?.limits?.[dayjs().format('MM-DD-YYYY')]) {
			yield put(
				userActions.userUpdateEmailsLeftLocal(
					response.data.data.limits[dayjs().format('MM-DD-YYYY')]
						.limit
				)
			);
		}
	} catch (error) {
		yield put(
			actions.emailSendAllFailure('Not able to validate email response.')
		);
	}
}

export function* getAISnippets({ payload }: EmailAction): SagaIterator {
	try {
		const { businessIds, listId, emailTemplate } = payload;
		const response = yield call(emailApi.getAISnippets, {
			businessIds,
			emailBody: emailTemplate.body
		});

		const { snippets, creditsAvailable, aiSnippetsId } = response.data.data;
		const aiText = {};

		snippets?.forEach(x => {
			const adjustedSnippet = x.aiSnippet
				.replace(/Sentence:/g, '')
				.replace(/Sentence to add:/g, '')
				.replace(/^"|"$/g, '')
				.trim();
			aiText[`${x.businessId}`] = {
				id: aiSnippetsId,
				value: emailTemplate.body.replace(
					/\[ai_snippet\]/g,
					adjustedSnippet
				)
			};
		});

		yield put(
			actions.emailGetAISnippetsSuccess({
				aiText,
				creditsAvailable,
				listId,
				emailTemplate
			})
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* regenerateAISnippet({ payload }: EmailAction): SagaIterator {
	try {
		const { businessId, listId, emailTemplate, aiSnippetsId } = payload;
		const response = yield call(emailApi.regenAISnippet, {
			businessId,
			emailBody: emailTemplate.body,
			aiSnippetsId
		});

		if (response.status !== 200) {
			yield put(
				actions.emailRegenAISnippetFailure({
					listId,
					emailTemplateId: emailTemplate.id,
					businessId
				})
			);
			return;
		}

		const { snippet, creditsAvailable } = response.data.data;

		const adjustedSnippet = snippet
			.replace(/Sentence:/g, '')
			.replace(/Sentence to add:/g, '')
			.replace(/^"|"$/g, '')
			.trim();
		const aiText = {
			[businessId]: {
				id: aiSnippetsId,
				value: emailTemplate.body.replace(
					/\[ai_snippet\]/g,
					adjustedSnippet
				)
			}
		};

		yield put(
			actions.emailRegenAISnippetSuccess({
				aiText,
				listId,
				emailTemplateId: emailTemplate.id,
				creditsAvailable,
				businessId
			})
		);
	} catch (error) {
		throw new Error(error);
	}
}

export function* getAICredits({ payload }: EmailAction): SagaIterator {
	try {
		const response = yield call(emailApi.getAICredits);

		yield put(actions.emailGetAICreditsSuccess(response.data.data));
	} catch (error) {
		throw new Error(error);
	}
}

export function* sendCustomerReportBadData({
	payload
}: EmailAction): SagaIterator {
	try {
		const response = yield call(
			emailApi.sendCustomerReportBadData,
			payload
		);

		if (response.status !== 200) {
			yield put(actions.emailSendCustomerReportBadDataFailure());
			return;
		}

		yield put(actions.emailSendCustomerReportBadDataSuccess());

		sendAnalytics({
			type: 'track',
			userId: payload.userId,
			eventName: 'User succesfully reported bad data',
			properties: {
				businessId: payload.businessId,
				incorrectData: payload.incorrectData,
				additionalString: payload.additionalString
			}
		});
	} catch (error) {
		throw new Error(error);
	}
}

export function* getMonthlyEmailLimit(): SagaIterator {
	try {
		const response = yield call(emailApi.getMonthlyEmailLimit);

		yield put(
			actions.emailGetMonthlyEmailLimitSuccess(
				response.data.data.monthlyLimit
			)
		);
	} catch (error) {
		throw new Error(error);
	}
}
